import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLogin } from '../../features/auth/useLogin';
import loginBg from '../../assets/login-bg.jpeg';
import logoImage from '../../assets/bg_transparent.png';
import './LoginPage.scss';

// (optional) tiny util to detect an invite redirect
const getInvitePath = (search: string) => {
  const sp = new URLSearchParams(search);
  const redirect = sp.get('redirect');
  if (!redirect) return null;
  try {
    const url = new URL(redirect, window.location.origin);
    return url.pathname.startsWith('/invite/') ? url.pathname : null;
  } catch {
    return null;
  }
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const loginMutation = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const invitePath = getInvitePath(location.search);

  // The API interceptor sends users here with ?reason=session_expired once the
  // 24h session cap is hit, so tell them why they were signed out.
  const sessionExpired = new URLSearchParams(location.search).get('reason') === 'session_expired';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    loginMutation.mutate(
      { email, password },
      {
        onSuccess: () => {
          const sp = new URLSearchParams(location.search);
          const redirect = sp.get('redirect');
          navigate(redirect || '/settings', { replace: true });
        },
      }
    );
  };

  return (
    <div className="login-page" style={{ backgroundImage: `url(${loginBg})` }}>
      <div className="login-card">
        <div className="logo">
          <img src={logoImage} alt="BULK MATERIAL PRO" className="logo-image" />
        </div>

        {sessionExpired && (
          <div
            className="card"
            style={{ marginBottom: 12, background: '#fffbeb', border: '1px solid #fde68a' }}
          >
            <div style={{ fontSize: 14 }}>Your session has expired. Please sign in again.</div>
          </div>
        )}

        {/* Optional banner if this login is part of an invite flow */}
        {invitePath && (
          <div
            className="card"
            style={{ marginBottom: 12, background: '#eff6ff', border: '1px solid #bfdbfe' }}
          >
            <div style={{ fontSize: 14 }}>
              You’re signing in to continue your invite flow. Please use the invited email address.
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Email Address"
            />
          </div>

          <div className="form-group">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Password"
            />
          </div>

          <div className="form-actions">
            <a href="/forgot-password" className="forgot-password">
              Forgot password?
            </a>
            <button type="submit" className="login-button" disabled={loginMutation.isPending}>
              {loginMutation.isPending ? 'Logging in…' : 'LOGIN'}
            </button>
          </div>

          {loginMutation.isError && (
            <div className="error-message">
              {(loginMutation.error as any)?.response?.data?.message ||
                'Login failed. Please try again.'}
            </div>
          )}
        </form>

        {/* Removed “Registration” button per your requirement */}
      </div>
    </div>
  );
};

export default LoginPage;
